import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CART_KEY = "swk_cart_v1";
const CartContext = createContext(null);

const readCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readCart);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = `${item.product_id}-${item.size || ""}-${item.color || ""}`;
      const next = [...prev];
      const idx = next.findIndex(
        (i) => `${i.product_id}-${i.size || ""}-${i.color || ""}` === key,
      );
      if (idx >= 0) {
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
      } else {
        next.push({ ...item, quantity: item.quantity || 1 });
      }
      return next;
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateQty = useCallback((idx, qty) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, quantity: Math.max(1, qty) } : it)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items],
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = {
    items,
    addItem,
    removeItem,
    updateQty,
    clear,
    subtotal,
    count,
    open,
    setOpen,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
